import type { MessageStatus } from '@blazeteams/sdk';

/**
 * Read-receipt (tick) state for OUTBOUND messages. Mirrors the backend's
 * `domain.MessageStatus` lifecycle: a message is created `pending`, the
 * provider acks it `sent`, then reports `delivered` and finally `read`.
 * `failed` is a terminal off-ramp (invalid number, provider rejection, …).
 *
 *   pending → sent → delivered → read
 *                 ↘ failed
 *
 * The DTO carries `status` plus `delivered_at` / `read_at` (both `omitempty`,
 * so the keys are simply absent until they apply). The WS event
 * `inbox:message_status` pushes the same transitions live.
 */

/**
 * The forward progression. `failed` is intentionally NOT in here — it isn't a
 * step "further along" the happy path, it's a separate terminal state handled
 * explicitly by `isStatusAdvance`.
 */
const PROGRESSION: readonly MessageStatus[] = ['pending', 'sent', 'delivered', 'read'];

/**
 * Position of a status on the delivery progression. Returns -1 for `undefined`
 * (unknown — below everything) and for `failed` (off the linear path). Used to
 * decide whether an incoming status moves the message FORWARD.
 */
export function statusRank(status: MessageStatus | undefined | null): number {
  if (!status) return -1;
  return PROGRESSION.indexOf(status);
}

/**
 * Whether a raw string is one of the five statuses we understand. Used at the
 * untrusted WS boundary to tell "unknown/typo" (worth a dev warning) apart
 * from "missing" — the ranking logic already treats both as "don't advance",
 * but conflating them hides backend bugs.
 */
export function isKnownStatus(status: string | undefined | null): boolean {
  return !!status && (PROGRESSION.includes(status as MessageStatus) || status === 'failed');
}

/**
 * Should an incoming status replace the current one? The rule the spec calls
 * "monotonía": a status may only move forward, so a `delivered` event that
 * arrives AFTER a `read` (out-of-order provider webhooks) is dropped.
 *
 *  - a missing `next` never applies (nothing to do)
 *  - `failed` always applies (terminal signal must surface)
 *  - once `failed`, non-`failed` updates are ignored (don't "un-fail")
 *  - otherwise apply only when strictly further along the progression
 *    (equal rank is a no-op so we don't reset timestamps with absent values)
 */
export function isStatusAdvance(
  prev: MessageStatus | undefined | null,
  next: MessageStatus | undefined | null,
): boolean {
  if (!next) return false;
  if (next === 'failed') return true;
  if (prev === 'failed') return false;
  return statusRank(next) > statusRank(prev);
}

/**
 * The single helper the UI derives a tick state from (per the spec:
 * "derivar el estado de un único helper"). Prefers the explicit `status`
 * enum; when the backend omitted it but stamped a timestamp (older rows,
 * providers that set times without the enum) it infers the furthest state
 * those timestamps imply. Returns `undefined` when nothing is known — the
 * caller renders no tick rather than a misleading one.
 */
export function messageStatusFrom(msg: {
  status?: MessageStatus;
  delivered_at?: string;
  read_at?: string;
}): MessageStatus | undefined {
  if (msg.status) return msg.status;
  if (msg.read_at) return 'read';
  if (msg.delivered_at) return 'delivered';
  return undefined;
}
